import axios from 'axios'
import config from 'config'

import {
  activities_path,
  activities_v2_path,
  activities_summary_path,
  user_profile_path,
  user_subscription_path,
  switch_to_annual_path,
  cancellation_feedback_path,
  user_achievements_path,
  update_user_profile_path,
  update_user_email_path,
  user_sign_out_path,
  activity_detail_path,
  create_user_ftp_path,
  edit_user_ftp_path,
  destroy_user_ftp_path,
  create_user_weight_path,
  edit_user_weight_path,
  destroy_user_weight_path,
  accept_ftp_suggestion_path,
  dismiss_ftp_suggestion_path,
  create_sign_in_link_path,
  validate_sign_in_link_path,
  validate_sign_in_code_path,
  delete_account_path,
  create_activity_path,
  upload_activity_path,
  edit_activity_path,
  destroy_activity_path,
  update_activity_note_path,
  all_activities_path,
  similar_activities_path,
  share_card_path,
  share_cards_path,
  share_activity_publicly_path,
  revoke_activity_share_path,
  unsubscribe_path,
  remove_stream_data_path
} from 'routes'

export default {
  getUser({ fresh = false } = {}) {
    // `fresh` bypasses the browser HTTP cache — used while polling for the Paddle
    // subscription to land after web checkout, so we never read a stale user.
    const options = fresh ? { params: { _: Date.now() }, headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } } : {}
    return axios.get(user_profile_path(), options).then((response) => response.data.user)
  },

  getSubscriptionPlans() {
    return config.subscription_plans
  },

  getActiveSubscriptionPlans() {
    return config.subscription_plans.filter((plan) => plan.active && !plan.trial)
  },

  getActiveSubscriptionPlansWithTrial() {
    return config.subscription_plans.filter((plan) => plan.active && plan.trial)
  },

  getUserSubscription() {
    return axios.get(user_subscription_path()).then((response) => response.data)
  },

  // Hardcoded paths: the `routes` module is generated at build time, so a
  // brand-new helper would not exist yet in an unbuilt tree.
  applyAsAmbassador(payload) {
    return axios.post('/api/v1/ambassadors', payload).then((response) => response.data)
  },

  getAmbassador() {
    return axios.get('/api/v1/ambassadors/me').then((response) => response.data)
  },

  switchToAnnualPlan() {
    return axios.post(switch_to_annual_path()).then((response) => response.data)
  },

  sendCancellationFeedback(reason, message) {
    return axios.post(cancellation_feedback_path(), { reason, message }).then((response) => response.data)
  },

  getActivity(id) {
    return axios.get(activity_detail_path({ id })).then((response) => response.data)
  },

  getActivities() {
    return axios.get(all_activities_path()).then((response) => response.data)
  },

  getAchievements() {
    return axios.get(user_achievements_path()).then((response) => response.data)
  },

  saveUserPreferences(preferences, options = {}) {
    const payload = {
      preferences: {
        sex: preferences.sex,
        measurement_preference: preferences.measurement_preference,
        max_hr: preferences.max_hr,
        rest_hr: preferences.rest_hr,
        lactate_threshold: preferences.lactate_threshold,
        email: preferences.email,
        email_notifications: preferences.email_notifications,
        running_ftp: preferences.running_ftp,
        swimming_ftp: preferences.swimming_ftp,
        beginning_of_week: preferences.beginning_of_week,
        auto_post_strava_description: preferences.auto_post_strava_description,
        push_ftp: preferences.push_ftp,
        push_achievements: preferences.push_achievements,
        push_care: preferences.push_care,
        push_opportunity: preferences.push_opportunity,
        ai_coach_personalization: preferences.ai_coach_personalization,
        timezone: preferences.timezone
      },
      goals: preferences.goals || [],
      weight: { weight: preferences.weight },
      power: { ftp: preferences.ftp },
      zones: preferences.zones,
      // Web Power step saves prefs but defers onboarding completion to the
      // subscription step (now last). Falsy everywhere else (mobile, Settings).
      defer_completion: Boolean(options.deferCompletion)
    }

    return axios.post(update_user_profile_path(), payload).then((response) => response.data)
  },

  // Silent timezone sync on app load. defer_completion keeps this boot-time call
  // from completing onboarding (and granting a trial) for not-yet-initialized users.
  saveTimezone(timezone) {
    return axios.post(update_user_profile_path(), { preferences: { timezone }, defer_completion: true }).then((response) => response.data)
  },

  // Finishes onboarding after the web subscription step. `subscribed: true` tells the
  // backend to initialize WITHOUT granting a trial, even if the Paddle webhook that
  // creates the subscription hasn't landed yet (the client successCallback races it).
  completeOnboarding() {
    return axios.post(update_user_profile_path(), { subscribed: true }).then((response) => response.data)
  },

  setAutoPostStravaDescription(enabled) {
    return axios
      .post(update_user_profile_path(), {
        preferences: { auto_post_strava_description: enabled }
      })
      .then((response) => response.data)
  },

  saveUserEmail(email, preferences = {}) {
    const payload = { email, preferences }

    return axios.post(update_user_email_path(), payload).then((response) => response.data)
  },

  createUserFTP(data) {
    const payload = {
      ftp: data.ftp,
      effective_from: data.effective_from
    }

    return axios.post(create_user_ftp_path(), payload).then((response) => response.data)
  },

  editUserFTP(data) {
    const payload = {
      id: data.id,
      ftp: data.ftp,
      effective_from: data.effective_from
    }

    return axios.post(edit_user_ftp_path(), payload).then((response) => response.data)
  },

  destroyUserFTP(data) {
    const payload = {
      id: data.id
    }

    return axios.post(destroy_user_ftp_path(), payload).then((response) => response.data)
  },

  acceptFtpSuggestion(data) {
    return axios.post(accept_ftp_suggestion_path(), { activity_record_id: data.activity_record_id }).then((response) => response.data)
  },

  dismissFtpSuggestion(data) {
    return axios.post(dismiss_ftp_suggestion_path(), { activity_record_id: data.activity_record_id }).then((response) => response.data)
  },

  createUserWeight(data) {
    const payload = {
      weight: data.weight,
      effective_from: data.effective_from
    }

    return axios.post(create_user_weight_path(), payload).then((response) => response.data)
  },

  editUserWeight(data) {
    const payload = {
      id: data.id,
      weight: data.weight,
      effective_from: data.effective_from
    }

    return axios.post(edit_user_weight_path(), payload).then((response) => response.data)
  },

  destroyUserWeight(data) {
    const payload = {
      id: data.id
    }

    return axios.post(destroy_user_weight_path(), payload).then((response) => response.data)
  },

  loadActivities() {
    return axios.get(activities_v2_path()).then((response) => response.data)
  },

  loadSummary() {
    return axios.get(activities_summary_path()).then((response) => response.data)
  },

  async createSignInLink(email) {
    const payload = {
      email: email
    }

    return (await axios.post(create_sign_in_link_path(), payload)).data
  },

  async validateSignInLink(token) {
    const payload = {
      token: token
    }

    return (await axios.post(validate_sign_in_link_path(), payload)).data
  },

  async validateSignInCode(email, code) {
    const payload = {
      email: email,
      code: code
    }

    return (await axios.post(validate_sign_in_code_path(), payload)).data
  },

  async createActivity(payload) {
    return (await axios.post(create_activity_path(), payload)).data
  },

  async destroyActivity(id) {
    const payload = {
      id
    }
    return (await axios.post(destroy_activity_path(), payload)).data
  },

  async editActivity(activity) {
    const payload = {
      ...activity
    }
    return (await axios.post(edit_activity_path(), payload)).data
  },

  async updateActivityNote(id, note) {
    const payload = {
      id,
      note
    }
    return (await axios.post(update_activity_note_path(), payload)).data
  },

  async removeStreamData(id, streamType) {
    const payload = { id, stream_type: streamType }
    return (await axios.post(remove_stream_data_path(), payload)).data
  },

  async getSimilarActivities(id) {
    const payload = {
      id
    }
    return (await axios.post(similar_activities_path(), payload)).data
  },

  getShareCard(id) {
    return axios.get(share_card_path({ id }), { responseType: 'blob' }).then((response) => response.data)
  },

  getShareCards(id) {
    return axios.get(share_cards_path({ id })).then((response) => response.data.cards)
  },

  shareActivityPublicly(id) {
    return axios.post(share_activity_publicly_path({ id })).then((response) => response.data)
  },

  revokeActivityShare(id) {
    return axios.post(revoke_activity_share_path({ id })).then((response) => response.data)
  },

  async uploadActivity(file) {
    let formData = new FormData()

    formData.append('file', file)

    const headers = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }

    return axios.post(upload_activity_path(), formData, headers).then((response) => response.data)
  },

  deleteAccount() {
    return axios.post(delete_account_path()).then((response) => response.data)
  },

  unsubscribeFromEmails(email, id) {
    const payload = {
      id,
      email
    }

    return axios.post(unsubscribe_path(), payload).then((response) => response.data)
  },

  signOut() {
    return axios.post(user_sign_out_path()).then((response) => response.data)
  }
}
